import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  StyleSheet,
  Text,
  View,
  TextInput,
  TouchableOpacity,
  Alert,
  BackHandler,
  ActivityIndicator,
  NativeEventSubscription,
} from "react-native";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { RouteProp } from "@react-navigation/native";
import { createOnlinePaymentV2, completeOnlinePaymentV2 } from "@services/api";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import { colors } from "@constants/theme";
import { fontFamily } from "@utils/styles";
import analytics from "@services/analytics";
import CustomText from "@components/CustomText";
import debounce from "@utils/debounce";
import useRazorpay, { RazorpaySuccessPayload } from "@hooks/useRazorpay";
import t from "@constants/localization";

type RootStackParamList = {
  CpOnlinePaymentV2: {
    ledger: {
      online_payment_screen_title?: string;
      online_payment_title?: string;
      online_payment_subtitle?: string;
      amount_to_collect_text?: string;
      amount_to_collect: string;
      error_text?: string;
      spokeName?: string;
      source?: string;
    };
  };
};

type CpOnlinePaymentV2NavigationProp = StackNavigationProp<
  RootStackParamList,
  "CpOnlinePaymentV2"
>;

type CpOnlinePaymentV2RouteProp = RouteProp<
  RootStackParamList,
  "CpOnlinePaymentV2"
>;

interface CpOnlinePaymentProps {
  navigation: CpOnlinePaymentV2NavigationProp;
  route: CpOnlinePaymentV2RouteProp;
}
const CpOnlinePaymentV2: React.FC<CpOnlinePaymentProps> = ({
  navigation,
  route,
}) => {
  const [amount, setAmount] = useState<string>("");
  const [isHandlingResponse, setIsHandlingResponse] = useState<boolean>(false);
  const [isCreatingPayment, setIsCreatingPayment] = useState<boolean>(false);
  const {
    online_payment_screen_title,
    online_payment_title,
    online_payment_subtitle,
    amount_to_collect_text,
    amount_to_collect,
    error_text,
    spokeName = "",
    source = "LEDGER",
  } = route.params?.ledger || {};
  const backHandler = useRef<NativeEventSubscription | null>(null);
  const orderIdRef = useRef<string | null>(null);
  const textInputRef = useRef<TextInput>(null);
  const btnLoadingRef = useRef(false);

  const handleSuccessResponse = async (result: RazorpaySuccessPayload) => {
    setIsHandlingResponse(true);
    try {
      const order_id = orderIdRef.current;
      const payload = {
        order_id,
        response_payload: result,
        source,
        spoke_name: spokeName,
        status: "SUCCESS",
      };
      const { data } = await completeOnlinePaymentV2(payload);
      return data?.message
        ? Alert.alert("", data.message, [
          {
            text: "OK",
            onPress: () => {
              navigation.pop();
            },
          },
        ])
        : null;
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Something went wrong!";
      return showPaymentFailedOrPendingAlert(errorMessage);
    } finally {
      setIsHandlingResponse(false);
    }
  };
  const handleErrorResponse = async (
    errorCode?: string | number,
    errorDescription?: string,
    errorObj?: Record<string, unknown>,
  ) => {
    setIsHandlingResponse(true);
    try {
      const order_id = orderIdRef.current;
      const payload = {
        order_id,
        response_payload: {
          code: errorCode,
          description: errorDescription,
          error: errorObj,
        },
        status: "FAILED",
        source,
        spoke_name: spokeName,
      };
      Alert.alert("", t("CpOnlinePayment/paymentFailed") || "Payment Failed", [
        {
          text: "OK",
          onPress: () => {
            navigation.pop();
          },
        },
      ]);
      await completeOnlinePaymentV2(payload);
      return;
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Something went wrong!";
      return showPaymentFailedOrPendingAlert(errorMessage);
    } finally {
      setIsHandlingResponse(false);
    }
  };
  const { processPayment } = useRazorpay({
    processSuccessResult: handleSuccessResponse,
    processErrorResult: handleErrorResponse,
  });

  const showError = useMemo(() => {
    return (
      amount && parseFloat(amount.slice(1)) > parseFloat(amount_to_collect)
    );
  }, [amount, amount_to_collect]);

  const createPayment = debounce(async () => {
    if (btnLoadingRef.current || isCreatingPayment) return;

    setIsCreatingPayment(true);
    btnLoadingRef.current = true;
    console.log('createPayment click');

    const floatAmount = parseFloat(amount.slice(1));

    if (Number.isNaN(floatAmount) || floatAmount <= 0) {
      Alert.alert("", "Please add a valid amount", [
        {
          text: "OK",
          onPress: () => {
            setAmount("₹");
          },
        },
      ]);
      setIsCreatingPayment(false);
      btnLoadingRef.current = false;
      return;
    }

    const payload = {
      amount: floatAmount,
      source,
      spoke_name: spokeName,
    };

    analytics.trackEvent("CREATE_PAYMENT_V2", payload);
    try {
      const { data } = await createOnlinePaymentV2(payload);
      orderIdRef.current = data.order_id;
      setIsCreatingPayment(false);
      await processPayment(data);
    } catch {
      // Error handled by processPayment error handler
      showPaymentFailedOrPendingAlert("Payment creation failed");
      setIsHandlingResponse(false); // Reset response loading on payment creation failure
    } finally {
      setIsCreatingPayment(false);
      btnLoadingRef.current = false;
    }
  }, 500);

  const showPaymentFailedOrPendingAlert = (message: string) => {
    Alert.alert("", message, [
      {
        text: "OK",
        onPress: () => {
          navigation.pop();
        },
      },
    ]);
  };

  const onBackPress = () => {
    // Prevent back navigation during payment creation or response handling
    if (isCreatingPayment || isHandlingResponse) {
      return true; // Block back navigation
    }
    return false; // Allow back navigation
  };

  // TODO only integer input
  useEffect(() => {
    // setInitiateResult(() => initiateResult);
    // setProcessResult(() => processResult);
    textInputRef.current?.focus();
    setAmount("₹");
    backHandler.current = BackHandler.addEventListener(
      "hardwareBackPress",
      onBackPress,
    );
    return () => {
      backHandler.current?.remove();
    };
  }, []);
  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle={online_payment_screen_title || ""}
        subHeaderTitle={undefined}
        headerStyle={undefined}
        headerColor={colors.white}
        hideShadow={false}
        navigation={navigation}
        route={route}
        rightIcon={undefined}
        RightButton={undefined}
        goingBackHandler={undefined}
        IsRightIconDropDown={undefined}
      />
      <View style={styles.cardContainer}>
        <View style={styles.innerContainer}>
          <CustomText variant="title_16">
            {online_payment_title || ""}
          </CustomText>
          <View style={styles.subtitleContainer}>
            <Text style={[styles.subtitle, styles.subtitleMargin]}>
              {online_payment_subtitle || ""}
            </Text>
            <Text style={[styles.subtitle, styles.subtitleBold]}>
              {`${amount_to_collect_text}`}
            </Text>
          </View>
          <TextInput
            style={styles.input}
            testID="amountTextInput"
            onChangeText={(val) => {
              // limiting to integer values only (0-9)
              // no invalid character allowed
              if (val === "₹" || val === "") return setAmount("₹");
              const regex = new RegExp("([0-9])");
              if (!regex.test(val.slice(-1))) return;
              setAmount(val);
            }}
            value={amount}
            keyboardType="numeric"
            ref={textInputRef}
            placeholder="₹"
            editable={!isHandlingResponse && !isCreatingPayment}
          />
          {showError ? (
            <Text style={styles.error}>{error_text || ""}</Text>
          ) : null}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[
                styles.paymentButton,
                btnLoadingRef.current || showError || isHandlingResponse || isCreatingPayment
                  ? styles.paymentButtonDisabled
                  : null,
              ]}
              testID="createPaymentButton"
              onPress={createPayment}
              disabled={!!(btnLoadingRef.current || showError || isHandlingResponse || isCreatingPayment)}
            >
              {btnLoadingRef.current || isCreatingPayment ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                <Text style={styles.paymentButtonText}>पेमेंट करें</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Payment Creation Loading Overlay */}
      {isCreatingPayment && (
        <View style={styles.loadingOverlay}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.plain_green} />
            <Text style={styles.loadingText}>
              Creating payment...
            </Text>
          </View>
        </View>
      )}

      {/* Response Loading Overlay */}
      {isHandlingResponse && (
        <View style={styles.loadingOverlay}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.plain_green} />
            <Text style={styles.loadingText}>
              Processing payment...
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  cardContainer: {
    backgroundColor: colors.white,
    padding: 10,
    alignItems: "center",
    elevation: 2,
    margin: 16,
    borderRadius: 10,
  },
  innerContainer: {
    alignItems: "center",
  },
  subtitleContainer: {
    flexDirection: "row",
    justifyContent: "center",
  },
  subtitle: {
    fontSize: 12,
    // fontFamily: fontFamily.semiBold,
  },
  subtitleMargin: {
    marginHorizontal: 4,
  },
  subtitleBold: {
    fontFamily: fontFamily.semiBold,
  },
  input: {
    width: 170,
    height: 75,
    color: colors.black,
    elevation: 1,
    padding: 15,
    fontSize: 20,
    fontFamily: fontFamily.semiBold,
    fontWeight: "bold",
  },
  error: {
    fontSize: 12,
    color: colors.errorRed,
    marginVertical: 4,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginVertical: 10,
  },
  paymentButton: {
    backgroundColor: colors.plain_green,
    paddingVertical: 8,
    paddingHorizontal: 12,
    flex: 1,
    alignItems: "center",
    borderRadius: 8,
  },
  paymentButtonDisabled: {
    backgroundColor: colors.silver,
  },
  paymentButtonText: {
    color: colors.white,
    fontSize: 14,
    fontFamily: fontFamily.semiBold,
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  loadingContainer: {
    backgroundColor: colors.white,
    padding: 20,
    borderRadius: 10,
    alignItems: "center",
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    fontFamily: fontFamily.semiBold,
    color: colors.black,
  },
});

export default CpOnlinePaymentV2;
